import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X, Copy, Check } from 'lucide-react';

/**
 * ErrorToast — notification globale d'erreur avec code copiable
 *
 * Usage depuis n'importe où dans le code :
 *   window.dispatchEvent(new CustomEvent('erp:error', { detail: {
 *     message: "Impossible de sauvegarder",
 *     code: "E-3AF9B2",        // optionnel, vient du backend
 *     status: 500              // optionnel
 *   }}))
 */

const ErrorToast = () => {
  const [errors, setErrors] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const addError = useCallback((e) => {
    const { message, code, status } = e.detail || {};
    const id = Date.now();
    setErrors(prev => [...prev.slice(-4), { id, message, code, status, ts: new Date() }]);

    // Auto-dismiss après 15s
    setTimeout(() => {
      setErrors(prev => prev.filter(err => err.id !== id));
    }, 15000);
  }, []);

  useEffect(() => {
    window.addEventListener('erp:error', addError);
    return () => window.removeEventListener('erp:error', addError);
  }, [addError]);

  const dismiss = (id) => setErrors(prev => prev.filter(e => e.id !== id));

  const copyCode = (id, code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (errors.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {errors.map(err => (
        <div
          key={err.id}
          className="bg-red-900 text-white rounded-lg shadow-xl border border-red-700 p-4 flex flex-col gap-2"
          role="alert"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-300 flex-shrink-0 mt-0.5" />
              <span className="text-sm font-medium">{err.message || 'Erreur inattendue'}</span>
            </div>
            <button
              onClick={() => dismiss(err.id)}
              className="text-red-300 hover:text-white flex-shrink-0"
            >
              <X size={14} />
            </button>
          </div>

          {err.code && (
            <div className="flex items-center gap-2 bg-red-950 rounded px-3 py-1.5">
              <span className="font-mono text-xs text-red-200 font-bold tracking-wider flex-1">
                {err.code}
              </span>
              <button
                onClick={() => copyCode(err.id, err.code)}
                className="text-red-300 hover:text-white"
                title="Copier le code d'erreur"
              >
                {copiedId === err.id
                  ? <Check size={13} className="text-green-400" />
                  : <Copy size={13} />
                }
              </button>
            </div>
          )}

          {err.code && (
            <p className="text-xs text-red-400">
              Communiquez ce code au support pour identifier le problème.
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default ErrorToast;
